import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { 
  Phone, Mail, Send, CheckCircle2, AlertCircle, Heart, Scale, Zap, Eye,
  ShieldAlert, ShieldCheck, AlertTriangle, Lock, Unlock, FileWarning, ChevronDown,
  ExternalLink
} from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";

const FOHSendPortal = () => {
  const { yestimateId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [deliveryResult, setDeliveryResult] = useState(null);
  const [yestimate, setYestimate] = useState(null);
  const [job, setJob] = useState(null);
  const navigate = useNavigate();
  
  // Customer contact info
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [preferredMethod, setPreferredMethod] = useState("sms");
  
  // ============== COMMUNICATION STYLE STATE ==============
  const [communicationStyle, setCommunicationStyle] = useState(null);
  const [styleError, setStyleError] = useState(false);
  
  // ============== JCE ENFORCEMENT STATE ==============
  const [jceCheck, setJceCheck] = useState(null);
  const [jceLoading, setJceLoading] = useState(true);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideComment, setOverrideComment] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideGranted, setOverrideGranted] = useState(false);
  
  // Communication style definitions
  const communicationStyles = [
    {
      id: "reassuring",
      label: "Reassuring",
      description: "Clarity, guidance & prevention framing",
      icon: Heart,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-500"
    },
    {
      id: "neutral",
      label: "Neutral",
      description: "Balanced professional delivery",
      icon: Scale,
      color: "text-slate-600",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-500"
    },
    {
      id: "positive",
      label: "Positive",
      description: "Confident & efficient delivery",
      icon: Zap,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-500"
    }
  ];
  
  // Navigate to pricing page and focus on specific job group
  const jumpToJob = (jobGroupId, jobGroupTitle) => {
    if (!yestimateId) return;
    
    // Navigate to pricing page with job group ID in URL parameter
    // The pricing page will read this and auto-expand/scroll to the job
    const pricingUrl = `/app/foh/yestimate/${yestimateId}/pricing?focusJob=${encodeURIComponent(jobGroupId)}`;
    toast.info(`Navigating to: ${jobGroupTitle?.substring(0, 40)}...`);
    navigate(pricingUrl);
  };
  
  useEffect(() => {
    loadData();
    loadJCECheck();
  }, [yestimateId]);
  
  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setYestimate(res.data.yestimate);
      setJob(res.data.job);
      const yestPhone = res.data.yestimate?.customer_phone;
      const jobPhone = res.data.job?.customer_phone;
      const customerDataPhone = res.data.customer?.phone;
      setCustomerPhone(yestPhone || jobPhone || customerDataPhone || "");
      setCustomerEmail(res.data.customer?.email || "");
      
      if (res.data.yestimate?.communication_style) {
        setCommunicationStyle(res.data.yestimate.communication_style);
      } else if (res.data.yestimate?.intent_config?.communication_style) {
        setCommunicationStyle(res.data.yestimate.intent_config.communication_style);
      }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };
  
  // ============== JCE ENFORCEMENT CHECK ==============
  const loadJCECheck = async () => {
    setJceLoading(true);
    try {
      const res = await axios.get(`${API}/jce/v3/send-check/${yestimateId}`);
      setJceCheck(res.data);
    } catch (err) {
      console.error("JCE check failed:", err);
      // If JCE check fails, allow sending (fail-open for now, can make stricter)
      setJceCheck({
        can_send: true,
        requires_override: false,
        score: 100,
        risk_level: "GREEN",
        risk_message: "JCE check unavailable - proceeding with send",
        is_admin: false
      });
    } finally {
      setJceLoading(false);
    }
  };
  
  // ============== OVERRIDE SUBMISSION ==============
  const handleOverrideSubmit = async () => {
    if (!overrideReason) {
      toast.error("Please select an override reason");
      return;
    }
    if (!overrideComment || overrideComment.trim().length < 10) {
      toast.error("Please provide a comment (minimum 10 characters)");
      return;
    }
    
    setOverrideSubmitting(true);
    try {
      const res = await axios.post(`${API}/jce/v3/override-and-send`, {
        estimate_id: yestimateId,
        override_reason_id: overrideReason,
        override_comment: overrideComment.trim(),
        jce_evaluation_snapshot: jceCheck?.evaluation_snapshot || {}
      });
      
      if (res.data.success) {
        setOverrideGranted(true);
        setShowOverrideModal(false);
        toast.success("Override granted", {
          description: "You may now send the estimate"
        });
      }
    } catch (err) {
      toast.error("Override failed", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setOverrideSubmitting(false);
    }
  };
  
  const handleSend = async () => {
    // Communication style should already be set via Customer Intent
    if (!communicationStyle) {
      toast.error("Communication Style Not Set", {
        description: "Return to Pricing Builder to configure Customer Intent first."
      });
      return;
    }
    
    // NEW LOGIC: Only block for TRUE blockers (Tier 1 locked items), not score
    const hasTrueBlockers = (jceCheck?.blocked_count || 0) > 0;
    if (hasTrueBlockers && !overrideGranted) {
      toast.error("Cannot send estimate", {
        description: "Locked items must be addressed or override required."
      });
      return;
    }
    
    if (preferredMethod === "sms" && !customerPhone) {
      toast.error("Please enter customer phone number for SMS delivery");
      return;
    }
    if (preferredMethod === "email" && !customerEmail) {
      toast.error("Please enter customer email for email delivery");
      return;
    }
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/send-portal-link`, {
        customer_phone: customerPhone,
        customer_email: customerEmail,
        preferred_method: preferredMethod,
        communication_style: communicationStyle
      });
      setDeliveryResult(res.data);
      setSent(true);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      // Handle object-type error detail (e.g., DRE blockers)
      if (errDetail && typeof errDetail === 'object') {
        const msg = errDetail.message || 'Sending blocked';
        const blockerList = errDetail.blockers;
        if (blockerList && Array.isArray(blockerList)) {
          const blockerText = blockerList.map(b => `${b.job_title || 'Job'}: ${b.reason || 'Review required'}`).join(', ');
          toast.error(msg, { description: blockerText, duration: 8000 });
        } else {
          toast.error(msg, { description: errDetail.resolution || 'Please review the estimate' });
        }
      } else {
        toast.error("Failed to send", {
          description: typeof errDetail === 'string' ? errDetail : 'An error occurred'
        });
      }
    } finally {
      setSending(false);
    }
  };
  
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const handlePreview = async () => {
    if (!communicationStyle) {
      toast.error("Communication style not configured", {
        description: "Return to Pricing Builder to set Customer Intent first."
      });
      return;
    }
    
    setPreviewLoading(true);
    try {
      // Save communication style (skip if yestimate already sent)
      try {
        await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
          tone: communicationStyle
        });
      } catch (putErr) {
        // Ignore if yestimate is already sent - we can still preview
      }
      
      // iter211ap — Preview button must mint a PREVIEW-ONLY token
      // (submission-blocked) so opening the customer view doesn't
      // create a real, submittable approval link. Previously we hit
      // `/approval-token` which minted a real customer token — any
      // click on "Approve Safety Only" in that tab silently moved the
      // job to Authorised without a portal ever being sent (VX17YXR).
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/preview-token`);
      const { token } = res.data;
      if (token) {
        window.open(`/approve/${token}`, '_blank');
      } else {
        toast.error("Failed to generate preview token");
      }
    } catch (err) {
      // iter198bb (Feb 2026) — Surface the real cause so FOH doesn't
      // misread a 5xx or network blip as a stale token.
      console.error('[FOHSendPortal] preview failed', {
        status:  err?.response?.status,
        data:    err?.response?.data,
        message: err?.message,
      });
      const status  = err?.response?.status;
      const detail  = err?.response?.data?.detail;
      let description;
      if (status === 404) {
        description = "Estimate not found — refresh the pricing screen and try again.";
      } else if (status >= 500) {
        description = `Server error (${status}). Try again in a moment.`;
      } else if (typeof detail === 'string') {
        description = detail;
      } else {
        description = "Please try again";
      }
      toast.error("Failed to generate preview", { description });
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const copyLink = () => {
    if (deliveryResult?.portal_url) {
      navigator.clipboard.writeText(deliveryResult.portal_url)
        .then(() => toast.success("Link copied to clipboard!"))
        .catch((err) => {
          console.error("Clipboard write failed:", err);
          toast.error("Please copy the link manually from the input field above.");
        });
    }
  };
  
  // ============== JCE SCORE BADGE COMPONENT ==============
  const JCEScoreBadge = () => {
    if (jceLoading) {
      return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
          <div className="h-8 bg-slate-200 rounded w-1/2"></div>
        </div>
      );
    }
    
    if (!jceCheck) return null;
    
    const { score, risk_level, risk_message, can_send, requires_override, is_admin, blocked_count } = jceCheck;
    
    // NEW: Only show blocking UI if there are TRUE blockers (Tier 1 locked items)
    const hasTrueBlockers = (blocked_count || 0) > 0;
    
    // Color mapping
    const colorMap = {
      GREEN: {
        bg: "bg-green-50",
        border: "border-green-400",
        text: "text-green-700",
        icon: ShieldCheck,
        iconColor: "text-green-600"
      },
      AMBER: {
        bg: "bg-amber-50",
        border: "border-amber-400",
        text: "text-amber-700",
        icon: AlertTriangle,
        iconColor: "text-amber-600"
      },
      RED: {
        bg: "bg-red-50",
        border: "border-red-400",
        text: "text-red-700",
        icon: ShieldAlert,
        iconColor: "text-red-600"
      }
    };
    
    const colors = colorMap[risk_level] || colorMap.GREEN;
    const Icon = colors.icon;
    
    // Check if send is blocked - only for TRUE blockers now
    const isBlocked = hasTrueBlockers && !overrideGranted;
    
    return (
      <div className={`rounded-lg p-4 border-2 ${colors.bg} ${colors.border}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${colors.bg}`}>
              <Icon className={`w-6 h-6 ${colors.iconColor}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${colors.text}`}>{score}%</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}>
                  {risk_level}
                </span>
              </div>
              <p className={`text-sm ${colors.text} mt-0.5`}>{risk_message}</p>
            </div>
          </div>
          
          {/* Override granted indicator */}
          {overrideGranted && hasTrueBlockers && (
            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 border border-green-300 rounded-full">
              <Unlock className="w-3 h-3 text-green-600" />
              <span className="text-xs font-medium text-green-700">Override Active</span>
            </div>
          )}
        </div>
        
        {/* TRUE BLOCKERS - Items that MUST be addressed */}
        {hasTrueBlockers && jceCheck.blocked_items?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-red-200">
            <div className="bg-red-100 rounded-lg p-3 mb-3">
              <p className="text-sm text-red-800 font-medium mb-2">
                🔒 {blocked_count} locked item(s) must be addressed:
              </p>
              <ul className="text-xs text-red-700 space-y-1.5">
                {jceCheck.blocked_items.slice(0, 5).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-1 group">
                    <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium">{item.name}</span>
                      {item.estimate_job_group_title && (
                        <span className="text-slate-500 ml-1">
                          (for: {item.estimate_job_group_title.length > 35 
                            ? item.estimate_job_group_title.substring(0, 35) + '...' 
                            : item.estimate_job_group_title})
                        </span>
                      )}
                    </div>
                    {item.estimate_job_group_id && (
                      <button
                        onClick={() => jumpToJob(item.estimate_job_group_id, item.estimate_job_group_title)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[10px] font-medium whitespace-nowrap"
                        title={`Go to: ${item.estimate_job_group_title}`}
                        data-testid={`jump-to-job-${idx}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Fix
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Override button for true blockers */}
            {!overrideGranted && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700">
                    Sending blocked - locked items missing
                  </span>
                </div>
                
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-400 text-red-700 hover:bg-red-100"
                  onClick={() => setShowOverrideModal(true)}
                  data-testid="jce-override-btn"
                >
                  <FileWarning className="w-4 h-4 mr-1" />
                  Override & Send
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* INFORMATIONAL: Missing items summary (non-blocking) */}
        {!hasTrueBlockers && (jceCheck.mandatory_count > 0 || jceCheck.recommended_count > 0) && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex gap-4 text-xs mb-2">
              {jceCheck.mandatory_count > 0 && (
                <span className="text-amber-600">
                  <strong>{jceCheck.mandatory_count}</strong> item(s) may need review
                </span>
              )}
              {jceCheck.recommended_count > 0 && (
                <span className="text-slate-500">
                  <strong>{jceCheck.recommended_count}</strong> optional suggestions
                </span>
              )}
            </div>
            
            {/* Expandable list of items needing review */}
            {jceCheck.mandatory_missing?.length > 0 && (
              <div className="mt-2 bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-xs font-semibold text-amber-800 mb-2">Items for review:</p>
                <ul className="text-xs text-amber-700 space-y-1.5" data-testid="review-items-list">
                  {jceCheck.mandatory_missing.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 group">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></span>
                      <div className="flex-1">
                        <span className="font-medium">{item.name}</span>
                        {item.estimate_job_group_title && (
                          <span className="text-slate-500 ml-1 text-[10px]">
                            ({item.estimate_job_group_title.length > 30 
                              ? item.estimate_job_group_title.substring(0, 30) + '...' 
                              : item.estimate_job_group_title})
                          </span>
                        )}
                      </div>
                      {item.estimate_job_group_id && (
                        <button
                          onClick={() => jumpToJob(item.estimate_job_group_id, item.estimate_job_group_title)}
                          className="flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[10px] font-medium whitespace-nowrap px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors"
                          title={`Go to: ${item.estimate_job_group_title}`}
                          data-testid={`review-jump-${idx}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {jceCheck.recommended_missing?.length > 0 && (
              <div className="mt-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs font-semibold text-slate-600 mb-2">Optional suggestions:</p>
                <ul className="text-xs text-slate-500 space-y-1.5" data-testid="optional-items-list">
                  {jceCheck.recommended_missing.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 group">
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <div className="flex-1">
                        <span>{item.name}</span>
                        {item.estimate_job_group_title && (
                          <span className="text-slate-400 ml-1 text-[10px]">
                            ({item.estimate_job_group_title.length > 30 
                              ? item.estimate_job_group_title.substring(0, 30) + '...' 
                              : item.estimate_job_group_title})
                          </span>
                        )}
                      </div>
                      {item.estimate_job_group_id && (
                        <button
                          onClick={() => jumpToJob(item.estimate_job_group_id, item.estimate_job_group_title)}
                          className="flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[10px] font-medium whitespace-nowrap px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors"
                          data-testid={`optional-jump-${idx}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <p className="text-xs text-slate-500 mt-2">
              These items can be managed in each job's pricing tab. Sending is allowed.
            </p>
          </div>
        )}
        
        {/* AMBER informational warning */}
        {risk_level === "AMBER" && !hasTrueBlockers && (
          <div className="mt-3 pt-3 border-t border-amber-200">
            <p className="text-xs text-amber-700">
              ℹ️ Some items may need attention. Review job cards if needed, but sending is allowed.
            </p>
          </div>
        )}
      </div>
    );
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  // ============== SUCCESS STATE ==============
  if (sent && deliveryResult) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="max-w-xl mx-auto p-6">
          <Card>
            <CardContent className="p-6 text-center">
              {deliveryResult.success ? (
                <>
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-green-700">Sent Successfully!</h2>
                  <p className="text-gray-600 mt-2">{deliveryResult.message}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Sent via {deliveryResult.method.toUpperCase()} to {deliveryResult.recipient}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full">
                    <span className="text-xs text-slate-500">Style:</span>
                    <span className="text-xs font-medium text-slate-700 capitalize">{communicationStyle}</span>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-amber-700">Manual Delivery Required</h2>
                  <p className="text-gray-600 mt-2">{deliveryResult.message}</p>
                </>
              )}
              
              <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                <Label className="text-sm text-slate-600">Customer Portal Link:</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={deliveryResult.portal_url} readOnly className="bg-white text-xs" data-testid="portal-url-input" />
                  <Button variant="outline" onClick={copyLink} data-testid="copy-link-btn">Copy</Button>
                </div>
              </div>
              
              <div className="mt-6 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => navigate("/app/foh")} data-testid="back-to-queue-btn">
                  Back to Queue
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    if (deliveryResult?.portal_url) {
                      window.open(deliveryResult.portal_url, '_blank');
                    } else {
                      toast.error("Portal URL not available. Please try sending again.");
                    }
                  }} 
                  data-testid="preview-portal-btn"
                >
                  Preview Portal
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // ============== MAIN SEND FORM ==============
  // Determine if send should be blocked - NEW: only for TRUE blockers
  const hasTrueBlockers = (jceCheck?.blocked_count || 0) > 0;
  const isSendBlocked = hasTrueBlockers && !overrideGranted;
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      {/* Override Modal - Inlined to prevent focus loss on state change */}
      {showOverrideModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-red-100 rounded-full">
                  <ShieldAlert className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Admin Override Required</h2>
                  <p className="text-sm text-slate-500">JCE Score: {jceCheck?.score}% (Below threshold)</p>
                </div>
              </div>
              
              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Compliance Notice:</strong> This override will be permanently logged with your name, 
                  timestamp, and the estimate snapshot for audit purposes.
                </p>
              </div>
              
              {/* Override Reason Select */}
              <div className="mb-4">
                <Label className="mb-2 block font-medium">
                  Override Reason <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <select
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 pr-10 appearance-none bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="override-reason-select"
                  >
                    <option value="">Select a reason...</option>
                    {jceCheck?.override_reasons?.map(reason => (
                      <option key={reason.id} value={reason.id}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
              
              {/* Override Comment */}
              <div className="mb-6">
                <Label className="mb-2 block font-medium">
                  Explanation <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={overrideComment}
                  onChange={(e) => setOverrideComment(e.target.value)}
                  placeholder="Provide specific justification for this override (minimum 10 characters)..."
                  className="min-h-[100px]"
                  data-testid="override-comment-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {overrideComment.length}/10 characters minimum
                </p>
              </div>
              
              {/* Missing items summary in modal - with job context */}
              {jceCheck?.mandatory_missing?.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 mb-6">
                  <p className="text-xs font-medium text-slate-700 mb-2">Items flagged by JCE:</p>
                  <ul className="text-xs text-slate-600 space-y-2">
                    {jceCheck.mandatory_missing.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 group">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <div className="flex-1">
                          <span className="font-medium">{item.name}</span>
                          {item.estimate_job_group_title && (
                            <div className="text-slate-500 text-[10px]">
                              → {item.estimate_job_group_title.length > 50 
                                ? item.estimate_job_group_title.substring(0, 50) + '...' 
                                : item.estimate_job_group_title}
                            </div>
                          )}
                        </div>
                        {item.estimate_job_group_id && (
                          <button
                            onClick={() => {
                              setShowOverrideModal(false);
                              jumpToJob(item.estimate_job_group_id, item.estimate_job_group_title);
                            }}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-[10px] font-medium whitespace-nowrap px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                            title={`Go to: ${item.estimate_job_group_title}`}
                            data-testid={`modal-jump-to-job-${idx}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                            Fix this
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowOverrideModal(false)}
                  disabled={overrideSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleOverrideSubmit}
                  disabled={overrideSubmitting || !overrideReason || overrideComment.length < 10}
                  data-testid="confirm-override-btn"
                >
                  {overrideSubmitting ? "Processing..." : "Confirm Override"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-xl mx-auto p-6">
        <Button variant="ghost" className="mb-4" onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/preview`)}>
          ← Back to Preview
        </Button>
        
        <Card>
          <CardHeader>
            <CardTitle>Send Inspection Results to Customer</CardTitle>
            <CardDescription>
              Send the Safety & Reliability Inspection portal link to the customer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Vehicle Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-semibold text-lg">{job?.reg}</p>
              <p className="text-slate-600">{job?.make} {job?.model}</p>
            </div>
            
            {/* ============== JCE ENFORCEMENT SCORE PANEL ============== */}
            <JCEScoreBadge />
            
            {/* ============== COMMUNICATION STYLE (READ-ONLY FROM INTENT CONFIG) ============== */}
            <div className="rounded-lg p-4 border-2 border-slate-200 bg-white">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-800">
                  Communication Style
                </Label>
                {communicationStyle && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Set in Customer Intent
                  </span>
                )}
              </div>
              
              {communicationStyle ? (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  {communicationStyle === 'reassuring' && <Heart className="w-5 h-5 text-blue-600" />}
                  {communicationStyle === 'neutral' && <Scale className="w-5 h-5 text-slate-600" />}
                  {communicationStyle === 'direct' && <Zap className="w-5 h-5 text-green-600" />}
                  <div>
                    <p className="font-semibold text-sm text-slate-800 capitalize">{communicationStyle}</p>
                    <p className="text-xs text-slate-500">
                      {communicationStyle === 'reassuring' && 'Softer phrasing, light explanations'}
                      {communicationStyle === 'neutral' && 'Clear and professional'}
                      {communicationStyle === 'direct' && 'Short, action-focused'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Communication style not set. Please configure Customer Intent in the Pricing Builder first.
                  </p>
                </div>
              )}
            </div>
            
            <Separator />
            
            {/* Delivery Method */}
            <div>
              <Label className="mb-3 block">Delivery Method</Label>
              <div className="flex gap-3">
                <Button
                  variant={preferredMethod === "sms" ? "default" : "outline"}
                  onClick={() => setPreferredMethod("sms")}
                  className="flex-1"
                >
                  <Phone className="w-4 h-4 mr-2" /> SMS (Recommended)
                </Button>
                <Button
                  variant={preferredMethod === "email" ? "default" : "outline"}
                  onClick={() => setPreferredMethod("email")}
                  className="flex-1"
                >
                  <Mail className="w-4 h-4 mr-2" /> Email (Fallback)
                </Button>
              </div>
            </div>
            
            {/* Phone Number */}
            {preferredMethod === "sms" && (
              <div>
                <Label>Customer Mobile Number *</Label>
                <Input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="07xxx xxxxxx"
                />
                <p className="text-xs text-slate-500 mt-1">
                  UK mobile number. Will receive a short message with the portal link.
                </p>
              </div>
            )}
            
            {/* Email */}
            {preferredMethod === "email" && (
              <div>
                <Label>Customer Email *</Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Email will contain only the portal link - no inspection content.
                </p>
              </div>
            )}
            
            <Separator />
            
            {/* What Customer Will Receive */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">What the customer will receive:</h4>
              {preferredMethod === "sms" ? (
                <p className="text-sm text-blue-700">
                  "Your Safety & Reliability Inspection results for {job?.reg} are ready. View and approve: [link]"
                </p>
              ) : (
                <p className="text-sm text-blue-700">
                  An email with a single button to view the inspection portal. No inspection details included.
                </p>
              )}
            </div>
            
            {/* Preview & Send Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handlePreview}
                disabled={previewLoading}
                data-testid="preview-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewLoading ? "Generating..." : "Preview Portal"}
              </Button>
              <Button
                className={`flex-1 ${isSendBlocked ? 'bg-slate-400 cursor-not-allowed' : ''}`}
                size="lg"
                onClick={handleSend}
                disabled={sending || !communicationStyle || isSendBlocked || jceLoading}
                data-testid="send-btn"
              >
                {sending ? "Sending..." : jceLoading ? "Checking..." : (
                  <>
                    {isSendBlocked && <Lock className="w-4 h-4 mr-2" />}
                    <Send className="w-4 h-4 mr-2" />
                    Send {preferredMethod === "sms" ? "SMS" : "Email"}
                  </>
                )}
              </Button>
            </div>
            
            {/* Status messages */}
            {!communicationStyle && !isSendBlocked && (
              <p className="text-center text-sm text-amber-600">
                Communication style not configured. Return to Pricing Builder to set Customer Intent.
              </p>
            )}
            
            {isSendBlocked && (
              <p className="text-center text-sm text-red-600 font-medium">
                🔒 Sending blocked - {jceCheck?.blocked_count || 0} locked item(s) must be addressed. {jceCheck?.is_admin ? "Use Override above." : "Contact an admin."}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FOHSendPortal;
